import { ChangeEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
	AlertMessage,
	AuthStatusStage,
	InlineAlertMessage,
	Navigation,
} from '@quizzach/components';
import {
	AuthPage,
	AuthResetPassField,
	AuthStatus,
	BtnTeal,
} from '@quizzach/styles';
import { IcCheck } from '@quizzach/assets';
import { ApiFailedReason, RoutePath } from '@quizzach/interfaces';
import { useResetPasswordStep2 } from '@quizzach/api-hooks';
import { scrollToTop } from '@quizzach/utils';
import { FaEye, FaEyeSlash } from 'react-icons/fa6';

export const ResetPasswordStep2 = () => {
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [buttonClicked, setButtonClicked] = useState<boolean>(false);

	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { t } = useTranslation();

	const {
		error,
		resetStep,
		state,
		setNewPassword,
		handleInputChange,
		validationErrorState,
	} = useResetPasswordStep2(pathname);

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setButtonClicked(true);

		if (!validationErrorState) {
			setNewPassword();
		}
	};

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	useEffect(() => {
		scrollToTop();
	}, []);

	return (
		<AuthPage data-id='reset-password-container'>
			<Navigation />

			<AuthStatus>
				<AuthStatusStage
					hand={t(
						resetStep === 'reset_success'
							? 'auth.status.newPassDone.hand'
							: 'auth.status.newPass.hand'
					)}
					title={t(
						resetStep === 'reset_success'
							? 'auth.resetPassword.step2.successTitle'
							: 'auth.resetPassword.step2.title'
					)}
				/>

				<div className='formcol'>
					<div className='inner'>
						{resetStep === 'reset_requesting' && (
							<>
								<p>{t('auth.resetPassword.step2.info')}</p>
								<form onSubmit={onSubmit}>
									<AuthResetPassField>
										<input
											placeholder={t('auth.fields.password')}
											type={showPassword ? 'text' : 'password'}
											name='password'
											value={state.password}
											onChange={(e: ChangeEvent<HTMLInputElement>) =>
												handleInputChange(e.target.name, e.target.value)
											}
										/>
										<span
											className='eye-icon'
											onClick={togglePasswordVisibility}
										>
											{showPassword ? <FaEye /> : <FaEyeSlash />}
										</span>
									</AuthResetPassField>
									{buttonClicked && validationErrorState && (
										<InlineAlertMessage
											message={validationErrorState}
											type='warning'
											className='validation-alert'
										/>
									)}
									{error && (
										<AlertMessage
											type='error'
											$fixed
											code={ApiFailedReason.default}
											style={{ width: '100%', marginBottom: '14px' }}
										/>
									)}
									<div style={{ marginTop: '18px' }}>
										<BtnTeal type='submit'>
											{t('auth.resetPassword.submit')}
										</BtnTeal>
									</div>
								</form>
							</>
						)}

						{resetStep === 'reset_success' && (
							<div className='done'>
								<div className='okrow' style={{ marginBottom: '18px' }}>
									<span className='oktick'>
										<IcCheck size={20} />
									</span>
									<p>{t('auth.resetPassword.step2.successInfo')}</p>
								</div>
								<BtnTeal
									type='button'
									onClick={() => navigate(RoutePath.Login)}
								>
									{t('auth.resetPassword.step2.login')}
								</BtnTeal>
							</div>
						)}
					</div>
				</div>
			</AuthStatus>
		</AuthPage>
	);
};
